import { useState } from "react";
import axios from "axios";
import { X, Loader2, Truck, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/useReveal";
import { BRAND } from "@/lib/content";
import hpFire from "@/assets/hp-fire.jpeg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "91" + BRAND.phone; // India country code + order number

export const CheckoutDialog = () => {
  const {
    items,
    subtotal,
    checkoutOpen,
    setCheckoutOpen,
    setConfirmedOrder,
    clearCart,
  } = useCart();

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!checkoutOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your name";
    if (!/^[6-9]\d{9}$/.test(form.phone.trim()))
      return "Please enter a valid 10-digit mobile number";
    if (form.address.trim().length < 10) return "Please enter your full address";
    if (!/^\d{6}$/.test(form.pincode.trim())) return "Please enter a valid 6-digit pincode";
    return null;
  };

  const placeOrder = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        payment_method: "COD",
        items: items.map((i) => ({
          pack_id: i.pack_id,
          title: i.title,
          qty: i.qty,
          price: i.price,
          mrp: i.mrp,
        })),
        subtotal,
        total: subtotal,
      };
      const { data } = await axios.post(`${API}/orders`, payload);

      // Build WhatsApp order message and open chat with the order number
      const lines = [
        "*New Order — Horse Power* 🐎",
        "",
        `*Order No:* ${data.order_number}`,
        "",
        "*Product(s):*",
        ...items.map(
          (i) => `• ${i.title} × ${i.qty} — ${formatINR(i.price * i.qty)}`
        ),
        "",
        `*Total: ${formatINR(subtotal)} (Cash on Delivery)*`,
        "",
        "*Customer Details:*",
        `Name: ${form.full_name}`,
        `Phone: ${form.phone}`,
        `Address: ${form.address}`,
        `City/State: ${[form.city, form.state].filter(Boolean).join(", ")}`,
        `Pincode: ${form.pincode}`,
      ];
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        lines.join("\n")
      )}`;
      window.open(waUrl, "_blank");

      setConfirmedOrder(data);
      clearCart();
      setCheckoutOpen(false);
      toast.success("Order sent on WhatsApp successfully!");
    } catch (e) {
      toast.error("Could not place order, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 sm:items-center">
      <div
        data-testid="checkout-dialog"
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white sm:rounded-2xl"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.94), rgba(255,255,255,0.97)), url(${hpFire})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 className="font-display text-lg text-neutral-900">Enter Order Details</h2>
          <button
            data-testid="checkout-close-button"
            onClick={() => setCheckoutOpen(false)}
            className="rounded-full p-2 hover:bg-neutral-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div>
            <Label htmlFor="full_name">Full Name *</Label>
            <Input
              id="full_name"
              data-testid="checkout-name-input"
              value={form.full_name}
              onChange={set("full_name")}
              placeholder="Your name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="phone">Mobile Number *</Label>
            <Input
              id="phone"
              data-testid="checkout-phone-input"
              value={form.phone}
              onChange={set("phone")}
              placeholder="10-digit number"
              inputMode="numeric"
              maxLength={10}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="address">Full Address *</Label>
            <Textarea
              id="address"
              data-testid="checkout-address-input"
              value={form.address}
              onChange={set("address")}
              placeholder="House no., street, area, landmark"
              className="mt-1"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" data-testid="checkout-city-input" value={form.city} onChange={set("city")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="state">State</Label>
              <Input id="state" data-testid="checkout-state-input" value={form.state} onChange={set("state")} className="mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="pincode">Pincode *</Label>
            <Input
              id="pincode"
              data-testid="checkout-pincode-input"
              value={form.pincode}
              onChange={set("pincode")}
              placeholder="6-digit pincode"
              inputMode="numeric"
              maxLength={6}
              className="mt-1"
            />
          </div>

          <div className="rounded-xl border border-neutral-200 bg-[var(--hp-page)] p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-neutral-800">
              <Truck className="h-4 w-4 text-[var(--hp-crimson)]" /> Payment Option
            </div>
            <div className="mt-2 rounded-lg border-2 border-[var(--hp-crimson)] bg-white px-3 py-2 text-sm font-semibold text-neutral-800">
              Cash on Delivery (COD) — order is booked on WhatsApp, pay at delivery
            </div>
          </div>
        </div>

        <div className="border-t border-neutral-200 px-5 py-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-neutral-600">Amount Payable</span>
            <span className="font-num text-2xl font-bold text-neutral-900">{formatINR(subtotal)}</span>
          </div>
          <Button
            data-testid="place-order-button"
            onClick={placeOrder}
            disabled={submitting}
            className="h-13 w-full bg-gradient-to-r from-emerald-600 to-emerald-700 py-4 text-base font-bold text-white hover:from-emerald-700 hover:to-emerald-800"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" /> Placing order...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" /> Order Now on WhatsApp
              </span>
            )}
          </Button>

          <div className="my-3 flex items-center gap-3">
            <span className="h-px flex-1 bg-neutral-200" />
            <span className="text-xs font-semibold text-neutral-400">OR</span>
            <span className="h-px flex-1 bg-neutral-200" />
          </div>

          <a
            href={`tel:${BRAND.phone}`}
            data-testid="call-to-order-button"
            className="flex h-13 w-full items-center justify-center gap-2 rounded-md border-2 border-[var(--hp-crimson)] py-3 text-base font-bold text-[var(--hp-crimson)] transition hover:bg-rose-50"
          >
            <Phone className="h-5 w-5" /> Call to Order — {BRAND.phone}
          </a>
          <p className="mt-2 text-center text-xs text-neutral-500">
            Order on WhatsApp or call us directly. Pay cash on delivery.
          </p>
        </div>
      </div>
    </div>
  );
};
